import { Button, cn } from "@app/ui";
import { useRef, useState } from "react";
import { pad } from "../../lib/format.js";
import { useI18n } from "../../lib/useI18n.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { ClockFace } from "./ClockFace.js";

/** Mobile gets a 30%-larger dial + digits for easier touch targeting. */
const CLOCK_SIZE_DEFAULT = 180;
const CLOCK_SIZE_MOBILE = Math.round(CLOCK_SIZE_DEFAULT * 1.3);

interface Props {
  hour: number | null;
  minute: number | null;
  onChange: (hour: number, minute: number) => void;
  onClear?: () => void;
}

export function TimePicker({ hour, minute, onChange, onClear }: Props) {
  const { t } = useI18n();
  const { hour12 } = useTimeFormat();
  const mobile = useMobileUx();
  const [mode, setMode] = useState<"hour" | "minute">("hour");
  const [editingField, setEditingField] = useState<"hour" | "minute" | null>(null);
  const [editText, setEditText] = useState("");
  const editRef = useRef<HTMLInputElement>(null);

  const has = hour !== null;
  const h24 = hour ?? 9;
  const m = minute ?? 0;
  const isPm = h24 >= 12;
  const h12 = ((h24 + 11) % 12) + 1;

  function setPm(pm: boolean) { onChange((h12 % 12) + (pm ? 12 : 0), m); }

  function handleClockSelect(v: number) {
    setEditingField(null);
    if (mode === "hour") {
      // 24h: clock yields the actual 0–23 hour; 12h: a 12-o'clock index.
      onChange(hour12 ? ((v === 0 ? 12 : v) % 12) + (isPm ? 12 : 0) : v, m);
    } else {
      onChange(h24, v);
    }
  }

  function handleClockDragEnd() {
    if (mode === "hour") setMode("minute");
  }

  function startEdit(field: "hour" | "minute") {
    setMode(field);
    setEditingField(field);
    setEditText(field === "hour" ? String(hour12 ? h12 : h24) : pad(m));
    setTimeout(() => editRef.current?.select(), 0);
  }

  function commitEdit() {
    if (editingField === "hour") {
      const n = parseInt(editText, 10);
      if (hour12) {
        if (n >= 1 && n <= 12) onChange((n % 12) + (isPm ? 12 : 0), m);
      } else if (n >= 0 && n <= 23) {
        onChange(n, m);
      }
    } else if (editingField === "minute") {
      const n = parseInt(editText, 10);
      if (!isNaN(n) && n >= 0 && n <= 59) onChange(h24, n);
    }
    setEditingField(null);
  }

  function handleEditKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") setEditingField(null);
    if (e.key === "Tab" && editingField === "hour") {
      e.preventDefault();
      commitEdit();
      startEdit("minute");
    }
  }

  const seg = cn("rounded-md font-mono tabular-nums", mobile ? "px-4 py-2 text-4xl" : "px-3 py-1.5 text-3xl");
  const inputW = mobile ? "w-24" : "w-[4.5rem]";
  const active = "bg-gradient-to-br from-indigo-500 to-pink-500 text-white shadow-sm";
  const inactive = "bg-muted text-foreground hover:bg-accent hover:scale-[1.03] active:scale-95 cursor-pointer transition-[transform,colors]";

  return (
    <div className="flex flex-col gap-2 rounded-md border border-input bg-card p-2.5 shadow-lg">
      <span className="text-[calc(10px*var(--app-text-scale))] uppercase tracking-wide text-muted-foreground">{t("taskForm.selectTime")}</span>

      <div className="flex items-stretch justify-center gap-2">
        {editingField === "hour" ? (
          <input
            ref={editRef}
            type="number" min={hour12 ? 1 : 0} max={hour12 ? 12 : 23}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleEditKeyDown}
            className={cn(seg, active, inputW, "text-center outline-none [appearance:textfield]")}
          />
        ) : (
          <button type="button" onClick={() => startEdit("hour")} className={cn(seg, mode === "hour" ? active : inactive)}>
            {pad(hour12 ? h12 : h24)}
          </button>
        )}

        <span className={cn("self-center bg-gradient-to-br from-indigo-500 to-pink-500 bg-clip-text font-light leading-none text-transparent", mobile ? "text-4xl" : "text-3xl")}>:</span>

        {editingField === "minute" ? (
          <input
            ref={editRef}
            type="number" min="0" max="59"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleEditKeyDown}
            className={cn(seg, active, inputW, "text-center outline-none [appearance:textfield]")}
          />
        ) : (
          <button type="button" onClick={() => startEdit("minute")} className={cn(seg, mode === "minute" ? active : inactive)}>
            {pad(m)}
          </button>
        )}

        {hour12 && (
          <div className="ml-2 flex flex-col gap-1 self-stretch">
            <Button type="button" size="sm" variant={!isPm ? "primary" : "outline"} onClick={() => setPm(false)}
              className={cn("h-full", !isPm && "border-0 bg-gradient-to-br from-indigo-500 to-pink-500 text-white")}>AM</Button>
            <Button type="button" size="sm" variant={isPm ? "primary" : "outline"} onClick={() => setPm(true)}
              className={cn("h-full", isPm && "border-0 bg-gradient-to-br from-indigo-500 to-pink-500 text-white")}>PM</Button>
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <ClockFace
          mode={mode}
          hours24={!hour12}
          selected={mode === "hour" ? (hour12 ? h12 % 12 : h24) : m}
          onSelect={handleClockSelect}
          onDragEnd={handleClockDragEnd}
          size={mobile ? CLOCK_SIZE_MOBILE : CLOCK_SIZE_DEFAULT}
        />
      </div>

      {onClear && has && (
        <div className="flex justify-end">
          <Button type="button" size="sm" variant="ghost" onClick={onClear}>{t("taskForm.clearTime")}</Button>
        </div>
      )}
    </div>
  );
}
